// src/views/admin/HelpdeskPage.js
// FlowSense MOW+ — Enterprise Helpdesk with Real-Time AI Assistance
// ─────────────────────────────────────────────────────────────────────────────
// FINAL MERGE NOTES (Doc 1 + Doc 2 → fully unified):
//
//   FROM DOC 1 (preserved):
//   - Dynamic AI Injector in generateAiInsights: unassigned workload detection +
//     critical/urgent auto-escalation payloads injected into baseInsights
//   - AI Action Router (handleAiAction): filter / auto_assign / escalate
//   - escalate uses POST /helpdesk/:id/escalate with { escalation_level, reason }
//
//   FROM DOC 2 (preserved):
//   - Full Filters panel (search, status, priority, category, my_tickets)
//   - handleFilterChange / clearFilters / hasActiveFilters
//   - handleRefresh with success toast
//   - handleOpenAssignModal
//   - handleResolveTicket
//   - Assign Ticket Modal
//   - Pagination UI
//   - 9-col table with AnimatePresence rows (MotionTr)
//   - Empty state in table
//   - Resolve Ticket button in modal footer
//   - Scrollbar CSS on chat container
//   - Extended SLA_STATUS_CONFIG (on_track, warning, critical, met)
//   - Details tab extra fields (Last Updated, Assigned Agent, Watchers)
//   - fetchAgents called on mount via useEffect
//   - setIsInternalNote(false) reset after send
//   - AI Analysis Tab (3rd tab) with AIQuickActionChips + breakdown grid
//
//   AI UI ELEMENTS (from Doc 2, zero new API calls):
//   - deriveSentiment / deriveBreachRisk / deriveTopicTags / deriveQuickActions
//   - AISentimentBadge — on table rows, message bubbles, modal header, new ticket form
//   - AIBreachRiskIndicator — pulsing badge below SLA progress bar
//   - AITopicTags — inline topic chips column + modal header + AI tab
//   - AIQuickActionChips — contextual action buttons in AI tab
//   - AIAnalysisStrip — gradient banner in modal body header
//   - AITypingIndicator — animated dots in page header during insight generation
//   - EnhancedStatsCard: aiHighlight prop + shimmer overlay
//   - SLAIndicator: AIBreachRiskIndicator embedded below bar
//   - MessageBubble: AISentimentBadge on each message
//   - Live sentiment preview in New Ticket modal
//
//   ADDITIVE-ONLY RULE: existing logic is NEVER altered.
//   All additions annotated // --- UPDATE / // --- END UPDATE
// ─────────────────────────────────────────────────────────────────────────────

import React, {
  useState, useEffect, useCallback, useMemo, useRef, memo,
} from 'react';
import {
  Box, Flex, Text, Button, Table, Thead, Tbody, Tr, Th, Td,
  Badge, Input, Select, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, useDisclosure, Textarea,
  VStack, HStack, Avatar, Spinner, useToast, IconButton,
  Menu, MenuButton, MenuList, MenuItem, InputGroup, InputLeftElement,
  Tabs, TabList, TabPanels, Tab, TabPanel, Tooltip, Progress,
  Stat, StatLabel, StatNumber, StatHelpText, SimpleGrid,
  Divider, Tag, TagLabel, TagLeftIcon, Skeleton, SkeletonText,
  FormControl, FormLabel, useColorModeValue, Alert, AlertIcon,
  AlertTitle, AlertDescription, Icon, Circle, useBreakpointValue,
  Wrap, WrapItem, Collapse,
} from '@chakra-ui/react';
import {
  MdCheckCircle, MdTimer, MdMessage, MdAdd, MdSearch, MdFilterList,
  MdRefresh, MdMoreVert, MdEdit, MdDelete, MdAssignment, MdClose,
  MdPerson, MdPriorityHigh, MdWarning, MdCheck, MdAttachFile,
  MdSend, MdVisibility, MdTrendingUp, MdDashboard,
  MdArrowUpward, MdArrowDownward, MdAccessTime, MdGroup,
  MdNotifications, MdErrorOutline, MdInfo, MdLightbulbOutline,
  MdBarChart, MdWaves, MdSignalCellularAlt, MdBuild,
  MdAutoAwesome, MdPsychology, MdAnalytics, MdInsights,
  MdSmartToy, MdElectricBolt, MdBolt, MdSpeed,
} from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';

// Services
import api                       from '../../services/api';
import webSocketService          from '../../services/websocketService';
import helpdeskAIInsightsService from '../../services/helpdeskAIInsightsService';
import aiService                 from '../../services/aiService';

// Components
import HelpdeskAIPanel      from '../../components/helpdesk/HelpdeskAIPanel';
import TicketWorkOrderCard  from '../../components/helpdesk/TicketWorkOrderCard';
// --- UPDATE (Phone AI Helpdesk) ---
import PhoneAICommandCenter from '../../components/helpdesk/PhoneAICommandCenter';
import ReportTracePanel     from '../../components/helpdesk/ReportTracePanel';
import phoneHelpdeskApi, { wsPayload } from '../../services/phoneHelpdeskApi';
// --- END UPDATE ---

// ─────────────────────────────────────────────────────────────────────────────
// MOTION WRAPPERS
// ─────────────────────────────────────────────────────────────────────────────
const MotionBox  = motion(Box);
const MotionFlex = motion(Flex);
const MotionTr   = motion.tr;

// ─────────────────────────────────────────────────────────────────────────────
// DEBUG LOGGER
// ─────────────────────────────────────────────────────────────────────────────
const DEBUG_ENABLED =
  process.env.REACT_APP_DEBUG_MODE === 'true' || process.env.NODE_ENV === 'development';

class DebugLogger {
  constructor(component) {
    this.component = component;
    this.logs      = [];
    this.maxLogs   = 200;
  }

  log(action, data = {}, level = 'info') {
    if (!DEBUG_ENABLED) return;
    const ts    = new Date().toISOString();
    const entry = { timestamp: ts, component: this.component, action, level, ...data };
    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) this.logs.shift();
    const styles = {
      info:    'color:#3182ce;font-weight:bold',
      success: 'color:#38a169;font-weight:bold',
      warning: 'color:#d69e2e;font-weight:bold',
      error:   'color:#e53e3e;font-weight:bold',
    };
    console.log(`%c[${this.component}:${action}]`, styles[level] || styles.info, entry);
  }

  getLogs() { return [...this.logs]; }
  clear()   { this.logs = []; }
}

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS & CONFIGURATION
// ─────────────────────────────────────────────────────────────────────────────
const PRIORITY_CONFIG = {
  critical: { color: 'red',    icon: MdErrorOutline, label: 'Critical' },
  urgent:   { color: 'orange', icon: MdPriorityHigh, label: 'Urgent'   },
  high:     { color: 'yellow', icon: MdWarning,      label: 'High'     },
  medium:   { color: 'blue',   icon: MdInfo,         label: 'Medium'   },
  low:      { color: 'gray',   icon: MdInfo,         label: 'Low'      },
};

const STATUS_CONFIG = {
  new:              { color: 'purple', label: 'New'              },
  open:             { color: 'blue',   label: 'Open'             },
  in_progress:      { color: 'cyan',   label: 'In Progress'      },
  pending_customer: { color: 'yellow', label: 'Pending Customer' },
  pending_internal: { color: 'orange', label: 'Pending Internal' },
  resolved:         { color: 'green',  label: 'Resolved'         },
  closed:           { color: 'gray',   label: 'Closed'           },
  cancelled:        { color: 'red',    label: 'Cancelled'        },
};

// --- UPDATE (Doc 2): Extended SLA_STATUS_CONFIG ---
const SLA_STATUS_CONFIG = {
  compliant: { color: 'green',  label: 'Compliant', icon: MdCheckCircle   },
  at_risk:   { color: 'orange', label: 'At Risk',   icon: MdWarning       },
  on_track:  { color: 'green',  label: 'On Track',  icon: MdCheck         },
  warning:   { color: 'yellow', label: 'Warning',   icon: MdWarning       },
  critical:  { color: 'orange', label: 'Critical',  icon: MdPriorityHigh  },
  breached:  { color: 'red',    label: 'Breached',  icon: MdErrorOutline  },
  met:       { color: 'green',  label: 'Met',       icon: MdCheckCircle   },
  no_sla:    { color: 'gray',   label: 'No SLA',    icon: MdInfo          },
};
// --- END UPDATE ---

// --- UPDATE (Doc 1 + Doc 2): infrastructure_damage + account_management added ---
const CATEGORY_OPTIONS = [
  { value: 'billing',               label: 'Billing'               },
  { value: 'service_outage',        label: 'Service Outage'        },
  { value: 'general_inquiry',       label: 'General Inquiry'       },
  { value: 'complaint',             label: 'Complaint'             },
  { value: 'feature_request',       label: 'Feature Request'       },
  { value: 'technical_support',     label: 'Technical Support'     },
  { value: 'account_management',    label: 'Account Management'    },
  { value: 'infrastructure_damage', label: 'Infrastructure Damage' },
];
// --- END UPDATE ---

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATION VARIANTS
// ─────────────────────────────────────────────────────────────────────────────
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, staggerChildren: 0.1 } },
  exit:    { opacity: 0, y: -20, transition: { duration: 0.3 } },
};

const cardVariants = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1, transition: { duration: 0.3 } },
  hover:   { y: -4, boxShadow: 'xl', transition: { duration: 0.2 } },
};

const statsVariants = {
  initial:  { opacity: 0, x: -20 },
  animate: (i) => ({ opacity: 1, x: 0, transition: { delay: i * 0.08, duration: 0.4 } }),
};

// --- UPDATE (Doc 2): AI tag spring animation variant ---
const aiTagVariants = {
  initial: { opacity: 0, scale: 0.8 },
  animate: { opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 300 } },
};
// --- END UPDATE ---

// ─────────────────────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────
const formatTimeAgo = (date) => {
  const s = Math.floor((new Date() - new Date(date)) / 1000);
  if (s < 60)     return 'Just now';
  if (s < 3600)   return `${Math.floor(s / 60)}m ago`;
  if (s < 86400)  return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(date).toLocaleDateString();
};

// ─────────────────────────────────────────────────────────────────────────────
// AI HELPER FUNCTIONS (derived purely from existing ticket fields — zero API calls)
// ─────────────────────────────────────────────────────────────────────────────

// --- UPDATE (Doc 2): AI sentiment derivation from ticket text ---
const deriveSentiment = (text = '') => {
  const t = text.toLowerCase();
  const negative = [
    'urgent', 'critical', 'broken', 'down', 'failed', 'cannot', 'unable',
    'outage', 'frustrated', 'angry', 'terrible', 'worst', 'poor',
  ];
  const positive = [
    'thank', 'appreciate', 'great', 'good', 'happy', 'resolved',
    'excellent', 'perfect', 'helpful',
  ];
  if (negative.some(w => t.includes(w))) return 'negative';
  if (positive.some(w => t.includes(w))) return 'positive';
  return 'neutral';
};

// AI breach risk prediction from SLA percentage + priority
const deriveBreachRisk = (ticket) => {
  if (!ticket) return 'unknown';
  if (ticket.sla_status === 'breached') return 'breached';
  const pct = ticket.percentage_elapsed || 0;
  if (pct >= 90 || ticket.priority === 'critical') return 'high';
  if (pct >= 70 || ticket.priority === 'urgent')   return 'medium';
  if (pct >= 50 || ticket.priority === 'high')     return 'elevated';
  return 'low';
};

// AI topic tag extraction from subject + description
const deriveTopicTags = (ticket) => {
  const text = `${ticket.subject || ''} ${ticket.description || ''}`.toLowerCase();
  const rules = [
    { tag: 'Water',     kw: ['water', 'leak', 'pipe', 'pressure', 'flow', 'wet']           },
    { tag: 'Billing',   kw: ['bill', 'payment', 'invoice', 'charge', 'refund', 'account']  },
    { tag: 'Outage',    kw: ['outage', 'service', 'down', 'offline', 'disconnected']        },
    { tag: 'Meter',     kw: ['meter', 'reading', 'usage', 'consumption']                    },
    { tag: 'Emergency', kw: ['emergency', 'urgent', 'critical', 'immediate', 'asap']        },
    { tag: 'Damage',    kw: ['damage', 'broken', 'crack', 'burst', 'flooded', 'flooding']   },
    { tag: 'Technical', kw: ['technical', 'system', 'software', 'error', 'fault']           },
  ];
  return rules
    .filter(r => r.kw.some(w => text.includes(w)))
    .map(r => r.tag)
    .slice(0, 3);
};

// AI quick-action suggestions based on ticket context
const deriveQuickActions = (ticket) => {
  if (!ticket) return [];
  const actions = [];
  if (
    !ticket.work_order_id &&
    ['service_outage', 'infrastructure_damage', 'technical_support'].includes(ticket.category)
  ) {
    actions.push({ label: 'Create Work Order',  color: 'orange', icon: MdBuild      });
  }
  if (!ticket.assigned_agent_id) {
    actions.push({ label: 'Auto-Assign Agent',  color: 'blue',   icon: MdPerson     });
  }
  if (['critical', 'urgent'].includes(ticket.priority)) {
    actions.push({ label: 'Escalate to Senior', color: 'red',    icon: MdArrowUpward });
  }
  if (ticket.sla_status === 'at_risk' || ticket.sla_status === 'warning') {
    actions.push({ label: 'Send SLA Warning',   color: 'yellow', icon: MdWarning    });
  }
  if (!actions.length) {
    actions.push({ label: 'AI Draft Reply',     color: 'purple', icon: MdAutoAwesome });
  }
  return actions.slice(0, 4);
};
// --- END UPDATE ---

// ─────────────────────────────────────────────────────────────────────────────
// AI UI SUB-COMPONENTS (enterprise visual elements)
// ─────────────────────────────────────────────────────────────────────────────

// --- UPDATE (Doc 2): AISentimentBadge — colour-coded sentiment chip ---
const AISentimentBadge = memo(({ text = '' }) => {
  const sentiment = deriveSentiment(text);
  const config = {
    negative: { label: '😤 Negative', scheme: 'red'   },
    positive: { label: '😊 Positive', scheme: 'green' },
    neutral:  { label: '😐 Neutral',  scheme: 'gray'  },
  };
  const cfg = config[sentiment] || config.neutral;
  return (
    <Tooltip label={`AI Sentiment: ${cfg.label}`} hasArrow>
      <Badge
        colorScheme={cfg.scheme}
        variant="subtle"
        fontSize="10px"
        px={2}
        py={0.5}
        borderRadius="full"
        cursor="default"
      >
        {cfg.label}
      </Badge>
    </Tooltip>
  );
});
AISentimentBadge.displayName = 'AISentimentBadge';
// --- END UPDATE ---

// --- UPDATE (Doc 2): AIBreachRiskIndicator — pulsing SLA risk prediction badge ---
const AIBreachRiskIndicator = memo(({ ticket }) => {
  const risk = deriveBreachRisk(ticket);
  if (risk === 'low' || risk === 'unknown') return null;

  const cfg = {
    breached: { color: 'red',    label: '🔴 Breached', animate: true  },
    high:     { color: 'red',    label: '⚠ High Risk', animate: true  },
    medium:   { color: 'orange', label: '◑ Med Risk',  animate: false },
    elevated: { color: 'yellow', label: '○ Elevated',  animate: false },
  };
  const c = cfg[risk] || cfg.medium;

  return (
    <Tooltip label={`AI Breach Prediction: ${c.label}`} hasArrow>
      <Badge
        colorScheme={c.color}
        variant="subtle"
        fontSize="9px"
        px={1.5}
        py={0.5}
        borderRadius="full"
        animation={c.animate ? 'pulse 1.5s infinite' : 'none'}
      >
        {c.label}
      </Badge>
    </Tooltip>
  );
});
AIBreachRiskIndicator.displayName = 'AIBreachRiskIndicator';
// --- END UPDATE ---

// --- UPDATE (Doc 2): AITopicTags — inline domain topic chips ---
const AITopicTags = memo(({ ticket, maxTags = 3 }) => {
  const tags = deriveTopicTags(ticket || {}).slice(0, maxTags);
  if (!tags.length) return null;

  return (
    <HStack spacing={1} flexWrap="wrap">
      {tags.map(tag => (
        <MotionBox key={tag} variants={aiTagVariants} initial="initial" animate="animate">
          <Tag size="sm" colorScheme="purple" variant="subtle" borderRadius="full" fontSize="9px" px={2}>
            <TagLeftIcon as={MdSmartToy} boxSize={2.5} />
            <TagLabel>{tag}</TagLabel>
          </Tag>
        </MotionBox>
      ))}
    </HStack>
  );
});
AITopicTags.displayName = 'AITopicTags';
// --- END UPDATE ---

// --- UPDATE (Doc 2): AIQuickActionChips — contextual action suggestion chips ---
const AIQuickActionChips = memo(({ ticket, onCreateWorkOrder, onReply }) => {
  const chipBg    = useColorModeValue('purple.50',  'purple.900');
  const chipBc    = useColorModeValue('purple.200', 'purple.700');
  const chipLabel = useColorModeValue('purple.700', 'purple.200');
  const actions   = deriveQuickActions(ticket);
  if (!actions.length) return null;

  return (
    <Box p={3} bg={chipBg} borderRadius="xl" borderWidth="1px" borderColor={chipBc}>
      <HStack mb={2} spacing={2}>
        <Icon as={MdPsychology} color="purple.500" boxSize={4} />
        <Text fontSize="xs" fontWeight="bold" color={chipLabel}>AI Suggested Actions</Text>
      </HStack>
      <Wrap spacing={2}>
        {actions.map((action, i) => (
          <WrapItem key={i}>
            <MotionBox whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                size="xs"
                colorScheme={action.color}
                variant="outline"
                leftIcon={<Icon as={action.icon} />}
                borderRadius="full"
                fontSize="xs"
                onClick={() => {
                  if (action.label.includes('Work Order') && onCreateWorkOrder) onCreateWorkOrder();
                  else if (action.label.includes('Draft') && onReply) onReply();
                }}
              >
                {action.label}
              </Button>
            </MotionBox>
          </WrapItem>
        ))}
      </Wrap>
    </Box>
  );
});
AIQuickActionChips.displayName = 'AIQuickActionChips';
// --- END UPDATE ---

// --- UPDATE (Doc 2): AIAnalysisStrip — gradient analysis banner in modal header ---
const AIAnalysisStrip = memo(({ ticket }) => {
  const sentiment  = deriveSentiment(`${ticket?.subject || ''} ${ticket?.description || ''}`);
  const risk       = deriveBreachRisk(ticket);
  const tags       = deriveTopicTags(ticket || {});

  const riskColor  = { breached: 'red', high: 'red', medium: 'orange', elevated: 'yellow', low: 'green' };
  const sentColor  = { negative: 'red', neutral: 'blue', positive: 'green' };
  const gradBg     = useColorModeValue('linear(to-r, purple.50, blue.50)', 'linear(to-r, purple.900, blue.900)');
  const stripBc    = useColorModeValue('purple.200', 'purple.700');
  const stripLabel = useColorModeValue('purple.700', 'purple.200');

  return (
    <Box
      bgGradient={gradBg}
      px={4} py={3}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={stripBc}
      mb={2}
    >
      <HStack justify="space-between" flexWrap="wrap" gap={2}>
        <HStack spacing={2}>
          <Icon as={MdAnalytics} color="purple.500" boxSize={4} />
          <Text fontSize="xs" fontWeight="bold" color={stripLabel}>AI Analysis</Text>
        </HStack>
        <HStack spacing={2} flexWrap="wrap">
          <Badge colorScheme={sentColor[sentiment] || 'gray'} borderRadius="full" fontSize="xs" px={2}>
            Sentiment: {sentiment}
          </Badge>
          <Badge colorScheme={riskColor[risk] || 'gray'} borderRadius="full" fontSize="xs" px={2}>
            Risk: {risk}
          </Badge>
          {tags.map(tag => (
            <Badge key={tag} colorScheme="purple" variant="subtle" borderRadius="full" fontSize="xs" px={2}>
              {tag}
            </Badge>
          ))}
        </HStack>
      </HStack>
    </Box>
  );
});
AIAnalysisStrip.displayName = 'AIAnalysisStrip';
// --- END UPDATE ---

// --- UPDATE (Doc 2): AITypingIndicator — animated dots during insight generation ---
const AITypingIndicator = memo(() => {
  const typeBg    = useColorModeValue('purple.50',  'purple.900');
  const typeBc    = useColorModeValue('purple.200', 'purple.700');
  const typeLabel = useColorModeValue('purple.700', 'purple.200');

  return (
    <HStack
      spacing={1}
      px={3} py={2}
      bg={typeBg}
      borderRadius="full"
      borderWidth="1px"
      borderColor={typeBc}
    >
      <Icon as={MdSmartToy} color="purple.500" boxSize={3} />
      <Text fontSize="xs" color={typeLabel}>AI thinking</Text>
      {[0, 0.15, 0.3].map((delay, i) => (
        <MotionBox
          key={i}
          w="4px" h="4px"
          bg="purple.500"
          borderRadius="full"
          animate={{ y: [0, -4, 0] }}
          transition={{ repeat: Infinity, duration: 0.6, delay }}
        />
      ))}
    </HStack>
  );
});
AITypingIndicator.displayName = 'AITypingIndicator';
// --- END UPDATE ---

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS (all wrapped with memo)
// ─────────────────────────────────────────────────────────────────────────────

const PriorityBadge = memo(({ priority }) => {
  const config        = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.medium;
  const IconComponent = config.icon;
  return (
    <Badge
      colorScheme={config.color}
      display="flex"
      alignItems="center"
      gap={1}
      px={2} py={1}
      borderRadius="md"
      fontSize="xs"
      fontWeight="bold"
    >
      <IconComponent size={14} />{config.label.toUpperCase()}
    </Badge>
  );
});
PriorityBadge.displayName = 'PriorityBadge';

const StatusBadge = memo(({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.open;
  return (
    <Badge
      colorScheme={config.color}
      variant="subtle"
      px={3} py={1}
      borderRadius="full"
      fontSize="xs"
      fontWeight="semibold"
    >
      {config.label}
    </Badge>
  );
});
StatusBadge.displayName = 'StatusBadge';

const SLAIndicator = memo(({ ticket }) => {
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400');
  const config         = SLA_STATUS_CONFIG[ticket.sla_status] || SLA_STATUS_CONFIG.no_sla;
  const IconComponent  = config.icon;

  if (ticket.sla_status === 'no_sla') return null;

  return (
    <VStack spacing={1} align="flex-start">
      <Tooltip label={`SLA ${config.label} — ${ticket.time_remaining_formatted || 'N/A'} remaining`} hasArrow>
        <HStack spacing={1}>
          <IconComponent color={`${config.color}.500`} size={16} />
          <Progress
            value={ticket.percentage_elapsed || 0}
            size="sm"
            colorScheme={config.color}
            w="60px"
            borderRadius="full"
          />
          <Text fontSize="xs" color={mutedTextColor}>{ticket.percentage_elapsed || 0}%</Text>
        </HStack>
      </Tooltip>
      {/* --- UPDATE (Doc 2): AI breach risk embedded below SLA bar --- */}
      <AIBreachRiskIndicator ticket={ticket} />
      {/* --- END UPDATE --- */}
    </VStack>
  );
});
SLAIndicator.displayName = 'SLAIndicator';

const EnhancedStatsCard = memo(({
  label, value, icon: IconComponent, color = 'blue',
  trend, isLoading, onClick, aiHighlight,
}) => {
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const labelColor  = useColorModeValue('gray.600', 'gray.400');
  const gradientBg  = useColorModeValue(
    `linear(to-br, ${color}.50, white)`,
    `linear(to-br, ${color}.900, gray.800)`
  );

  return (
    <MotionBox
      whileHover="hover"
      variants={cardVariants}
      cursor={onClick ? 'pointer' : 'default'}
      onClick={onClick}
    >
      <Box
        bgGradient={gradientBg}
        p={5}
        borderRadius="2xl"
        borderWidth="1px"
        borderColor={aiHighlight ? `${color}.400` : borderColor}
        boxShadow={aiHighlight ? `0 0 0 2px var(--chakra-colors-${color}-300)` : 'md'}
        position="relative"
        overflow="hidden"
        transition="all 0.2s"
        _hover={{ borderColor: `${color}.500`, transform: onClick ? 'translateY(-2px)' : 'none' }}
      >
        <Box
          position="absolute"
          top="-20px" right="-20px"
          width="100px" height="100px"
          bg={`${color}.100`}
          opacity={0.2}
          borderRadius="full"
          filter="blur(20px)"
        />
        {/* --- UPDATE (Doc 2): shimmer overlay during AI recalculation --- */}
        {isLoading && (
          <Box position="absolute" inset="0" borderRadius="2xl" overflow="hidden">
            <MotionBox
              position="absolute"
              top="0" left="-100%"
              h="100%" w="60%"
              bgGradient="linear(to-r, transparent, whiteAlpha.400, transparent)"
              animate={{ left: ['-100%', '200%'] }}
              transition={{ repeat: Infinity, duration: 1.2, ease: 'linear' }}
            />
          </Box>
        )}
        {/* --- END UPDATE --- */}
        {isLoading ? (
          <Skeleton height="80px" />
        ) : (
          <Stat position="relative" zIndex={1}>
            <Flex justify="space-between" align="center">
              <Box>
                <StatLabel color={labelColor} fontSize="sm" fontWeight="600" mb={2}>
                  {label}
                </StatLabel>
                <StatNumber fontSize="3xl" fontWeight="bold" color={`${color}.600`}>
                  {value}
                </StatNumber>
                {trend !== undefined && (
                  <StatHelpText mb={0} mt={2}>
                    <HStack spacing={1}>
                      {trend > 0
                        ? <MdArrowUpward color="green" size={16} />
                        : trend < 0
                        ? <MdArrowDownward color="red" size={16} />
                        : null}
                      <Text fontSize="xs">{Math.abs(trend)}% vs last period</Text>
                    </HStack>
                  </StatHelpText>
                )}
              </Box>
              <Circle size="60px" bg={`${color}.500`} boxShadow="md">
                <IconComponent size={28} color="white" />
              </Circle>
            </Flex>
          </Stat>
        )}
      </Box>
    </MotionBox>
  );
});
EnhancedStatsCard.displayName = 'EnhancedStatsCard';

const MessageBubble = memo(({ message, isAgent }) => {
  const bgColor   = useColorModeValue(isAgent ? 'blue.50' : 'gray.50', isAgent ? 'blue.900' : 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');

  return (
    <MotionBox
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      maxW="80%"
      alignSelf={isAgent ? 'flex-end' : 'flex-start'}
    >
      <Box bg={bgColor} p={4} borderRadius="xl" boxShadow="sm">
        <HStack spacing={2} mb={2} flexWrap="wrap">
          <Avatar size="xs" name={message.sender_name || 'User'} src={message.sender_avatar} />
          <Text fontSize="xs" fontWeight="bold" color={textColor}>
            {message.sender_name || 'User'}
          </Text>
          <Text fontSize="xs" color="gray.500">{formatTimeAgo(message.created_at)}</Text>
          {message.is_internal_note && (
            <Badge colorScheme="purple" fontSize="xs">Internal</Badge>
          )}
          {/* --- UPDATE (Doc 2): AI sentiment badge on individual messages --- */}
          <AISentimentBadge text={message.message_body || ''} />
          {/* --- END UPDATE --- */}
        </HStack>
        <Text fontSize="sm" color={textColor} whiteSpace="pre-wrap">
          {message.message_body}
        </Text>
        {message.attachment_url && (
          <HStack mt={2} spacing={2}>
            <MdAttachFile size={16} />
            <Text fontSize="xs" color="blue.600" cursor="pointer">
              {message.attachment_name || 'Attachment'}
            </Text>
          </HStack>
        )}
      </Box>
    </MotionBox>
  );
});
MessageBubble.displayName = 'MessageBubble';

const ConnectionIndicator = memo(({ isConnected }) => {
  const pulseKf = `@keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}`;
  const bg      = useColorModeValue(
    isConnected ? 'green.50'  : 'red.50',
    isConnected ? 'green.900' : 'red.900'
  );
  const bc      = useColorModeValue(
    isConnected ? 'green.200' : 'red.200',
    isConnected ? 'green.700' : 'red.700'
  );

  return (
    <>
      <style>{pulseKf}</style>
      <Tooltip label={isConnected ? 'Live updates enabled' : 'Disconnected'} hasArrow>
        <HStack spacing={2} px={3} py={1} borderRadius="full" bg={bg} borderWidth="1px" borderColor={bc}>
          <Circle
            size="8px"
            bg={isConnected ? 'green.500' : 'red.500'}
            animation={isConnected ? 'pulse 2s infinite' : 'none'}
          />
          <Text fontSize="xs" fontWeight="600" color={isConnected ? 'green.700' : 'red.700'}>
            {isConnected ? 'Live' : 'Offline'}
          </Text>
        </HStack>
      </Tooltip>
    </>
  );
});
ConnectionIndicator.displayName = 'ConnectionIndicator';

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function HelpdeskPage() {
  const logger = useRef(new DebugLogger('HelpdeskPage')).current;

  useEffect(() => {
    logger.log('mount',   { timestamp: new Date().toISOString() });
    return () => logger.log('unmount', { timestamp: new Date().toISOString() });
  }, [logger]);

  // ── Theme tokens (all hoisted — no conditional hook calls) ────────────────
  const bgColor        = useColorModeValue('gray.50',  'gray.900');
  const cardBgColor    = useColorModeValue('white',    'gray.800');
  const textColor      = useColorModeValue('gray.800', 'white');
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor    = useColorModeValue('gray.200', 'gray.700');
  const hoverBg        = useColorModeValue('gray.50',  'gray.700');
  const inputBg        = useColorModeValue('white',    'gray.700');
  const tableHeaderBg  = useColorModeValue('gray.50',  'gray.700');
  const tableHoverBg   = useColorModeValue('rgba(0,0,0,0.02)', 'rgba(255,255,255,0.02)');
  const modalBodyBg    = useColorModeValue('gray.50',  'gray.900');
  const headerGradient = useColorModeValue(
    'linear(to-r, blue.500, purple.600)',
    'linear(to-r, blue.400, purple.500)'
  );
  // --- UPDATE (Doc 2): AI analysis box tokens hoisted ---
  const aiBoxBg    = useColorModeValue('blue.50',  'blue.900');
  const aiBoxBc    = useColorModeValue('blue.200', 'blue.700');
  const aiBoxLabel = useColorModeValue('blue.700', 'blue.200');
  // --- END UPDATE ---

  const isMobile = useBreakpointValue({ base: true, md: false });

  // ── State ──────────────────────────────────────────────────────────────────
  const [tickets,           setTickets]           = useState([]);
  const [selectedTicket,    setSelectedTicket]    = useState(null);
  const [messages,          setMessages]          = useState([]);
  const [stats,             setStats]             = useState(null);
  const [agents,            setAgents]            = useState([]);
  const [selectedAgentId,   setSelectedAgentId]   = useState('');

  const [loading,           setLoading]           = useState(true);
  const [loadingMessages,   setLoadingMessages]   = useState(false);
  const [submitting,        setSubmitting]        = useState(false);
  const [isRefreshing,      setIsRefreshing]      = useState(false);

  const [wsConnected,       setWsConnected]       = useState(false);
  const [lastWsUpdate,      setLastWsUpdate]      = useState(null);

  const [aiInsights,        setAiInsights]        = useState([]);
  const [isAiLoading,       setIsAiLoading]       = useState(false);
  const [showAiPanel,       setShowAiPanel]       = useState(true);

  const [filters,           setFilters]           = useState({
    status: '', priority: '', category: '', search: '', my_tickets: false,
  });
  const [pagination,        setPagination]        = useState({ limit: 25, offset: 0, total: 0 });

  const [newTicketForm,     setNewTicketForm]     = useState({
    subject: '', description: '', category: 'general_inquiry', priority: 'medium',
  });
  const [replyText,         setReplyText]         = useState('');
  const [isInternalNote,    setIsInternalNote]    = useState(false);
  const [isGeneratingReply, setIsGeneratingReply] = useState(false);

  const { isOpen: isDetailOpen,    onOpen: onDetailOpen,    onClose: onDetailClose    } = useDisclosure();
  const { isOpen: isNewTicketOpen, onOpen: onNewTicketOpen, onClose: onNewTicketClose } = useDisclosure();
  const { isOpen: isAssignOpen,    onOpen: onAssignOpen,    onClose: onAssignClose    } = useDisclosure();

  const [selectedTab, setSelectedTab] = useState(0);
  // --- UPDATE (Phone AI Helpdesk): command center visibility (remembered per browser) ---
  const [showPhoneCenter, setShowPhoneCenter] = useState(() => {
    try { return localStorage.getItem('hd_phone_center') !== '0'; } catch (_) { return true; }
  });
  const togglePhoneCenter = () => setShowPhoneCenter((v) => {
    try { localStorage.setItem('hd_phone_center', v ? '0' : '1'); } catch (_) { /* ignore */ }
    return !v;
  });
  // --- END UPDATE ---
  const toast = useToast();

  // ── DATA FETCHING ──────────────────────────────────────────────────────────
  const fetchTickets = useCallback(async (isBackground = false) => {
    if (!isBackground) {
      setLoading(true);
      logger.log('fetchTickets:start', { filters, pagination });
    }
    try {
      const queryParams = new URLSearchParams({
        ...filters,
        limit:  pagination.limit,
        offset: pagination.offset,
      });
      const response = await api.get(`/helpdesk?${queryParams}`);

      if (response.data.success) {
        const newTickets = response.data.data || [];
        setTickets(newTickets);

        let statsData = response.data.metrics || response.data.stats;
        if (!statsData) {
          statsData = {
            total:            newTickets.length,
            active_tickets:   newTickets.filter(t =>
              ['new', 'open', 'in_progress', 'pending_customer', 'pending_internal'].includes(t.status)
            ).length,
            resolved_tickets: newTickets.filter(t =>
              ['resolved', 'closed'].includes(t.status)
            ).length,
            breached:         newTickets.filter(t => t.sla_status === 'breached').length,
          };
        }
        setStats(statsData);
        setPagination(prev => ({ ...prev, total: response.data.count || newTickets.length }));
      }
    } catch (error) {
      logger.log('fetchTickets:error', { error: error.message }, 'error');
      if (!isBackground) {
        toast({
          title:       'Error',
          description: error.response?.data?.message || 'Failed to fetch tickets',
          status:      'error',
          duration:    5000,
          isClosable:  true,
        });
      }
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [filters, pagination.limit, pagination.offset, toast, logger]);

  const fetchTicketDetails = async (ticketId) => {
    logger.log('fetchTicketDetails:start', { ticketId });
    setLoadingMessages(true);
    try {
      const response = await api.get(`/helpdesk/${ticketId}?include_activities=false`);
      if (response.data.success) {
        setSelectedTicket(response.data.ticket);
        setMessages(response.data.messages || []);
        setTimeout(() => {
          const chatBox = document.getElementById('chat-container');
          if (chatBox) chatBox.scrollTop = chatBox.scrollHeight;
        }, 150);
      }
    } catch (error) {
      toast({
        title:       'Error',
        description: 'Failed to fetch ticket details',
        status:      'error',
        duration:    3000,
        isClosable:  true,
      });
    } finally {
      setLoadingMessages(false);
    }
  };

  const fetchAgents = useCallback(async () => {
    try {
      const response = await api.get('/users?role=admin,staff&limit=100');
      if (response.data?.data) setAgents(response.data.data);
    } catch (error) {
      logger.log('fetchAgents:error', { error: error.message }, 'error');
    }
  }, [logger]);

  // ── WEBSOCKET ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const handleWsStatus = (statusEvent) => setWsConnected(statusEvent.status === 'connected');

    // --- UPDATE (Phone AI Helpdesk): the ws server sends {type, payload}; the fields this handler
    // reads (event, ticket_id, …) are inside payload — unwrap so live updates actually fire.
    const handleHelpdeskUpdate = (rawMessage) => {
      const message = wsPayload(rawMessage) || {};
      // --- END UPDATE
      setLastWsUpdate(new Date());

      if (message.event === 'new_message') {
        if (isDetailOpen && selectedTicket?.id === message.ticket_id) {
          setMessages(prev =>
            prev.some(m => m.id === message.reply.id) ? prev : [...prev, message.reply]
          );
          setTimeout(() => {
            const c = document.getElementById('chat-container');
            if (c) c.scrollTop = c.scrollHeight;
          }, 100);
        } else {
          toast({
            title:       `New Message: Ticket ${message.ticket_number}`,
            description: 'A customer or agent has replied.',
            status:      'info',
            position:    'top-right',
            duration:    4000,
            isClosable:  true,
            variant:     'left-accent',
          });
        }
        fetchTickets(true);
      } else if (
        ['ticket_created', 'ticket_updated', 'ticket_assigned', 'ticket_resolved']
          .includes(message.event)
      ) {
        fetchTickets(true);
        toast({
          title:       'Ticket Updated',
          description: message.description ||
            `Ticket ${message.ticket_number || ''} was ${message.event.replace('ticket_', '')}`,
          status:      'info',
          position:    'top-right',
          duration:    3000,
          isClosable:  true,
          variant:     'left-accent',
        });
        if (isDetailOpen && selectedTicket?.id === message.ticket_id) {
          fetchTicketDetails(message.ticket_id);
        }
      // --- UPDATE (Phone AI Helpdesk): phone / public report lifecycle events
      } else if (
        ['phone_report_created', 'phone_report_geotagged', 'report_verified', 'work_order_linked', 'inspection_scheduled', 'call_ended']
          .includes(message.event)
      ) {
        fetchTickets(true);
        if (isDetailOpen && message.ticket_id && selectedTicket?.id === message.ticket_id) {
          fetchTicketDetails(message.ticket_id);
        }
      // --- END UPDATE
      }
    };

    webSocketService.connect();
    webSocketService.subscribe('status',   handleWsStatus);
    webSocketService.subscribe('helpdesk', handleHelpdeskUpdate);

    return () => {
      webSocketService.unsubscribe('status',   handleWsStatus);
      webSocketService.unsubscribe('helpdesk', handleHelpdeskUpdate);
    };
  }, [fetchTickets, isDetailOpen, selectedTicket, toast]);

  // ── GLOBAL AI INSIGHTS + INJECTOR (Doc 1 dynamic payload builder) ──────────
  const generateAiInsights = useCallback(async (forceRefresh) => {
    if (!tickets.length || !stats) return;
    setIsAiLoading(true);

    try {
      // --- UPDATE (AI Insights v3): server engine (SQL + ML forecast + phone channel + feedback) merged with local analysis
      let baseInsights = (await helpdeskAIInsightsService.getMergedInsights(tickets, stats, { refresh: forceRefresh === true })) || [];
      const hasKey = (k) => baseInsights.some(i => i.key === k);
      // --- END UPDATE

      // --- UPDATE (Doc 1): Dynamic AI Injector — unassigned workload ---
      const unassignedTickets = tickets.filter(
        t => !t.assigned_agent_id && ['new', 'open'].includes(t.status)
      );
      if (unassignedTickets.length > 0 && !hasKey('hd.unassigned')) { // --- UPDATE: no duplicate card
        baseInsights.unshift({
          id:            'helpdesk:hd.unassigned_injected',
          title:         'Unassigned Workload Detected',
          message:       `There are ${unassignedTickets.length} open tickets unassigned. AI can optimize routing based on agent workload.`,
          icon:          'MdGroup',
          severity:      'warning',
          action:        'Auto-Assign Agents',
          actionType:    'auto_assign',
          actionPayload: { ticketIds: unassignedTickets.map(t => t.id) },
          metrics:       { count: unassignedTickets.length, percentage: Number(((unassignedTickets.length / tickets.length) * 100).toFixed(1)) }, // --- UPDATE: measured, not a fixed 85
        });
      }
      // --- END UPDATE ---

      // --- UPDATE (Doc 1): Dynamic AI Injector — critical/urgent escalation ---
      const criticalAtRisk = tickets.filter(
        t => (t.priority === 'critical' || t.priority === 'urgent') &&
             t.status !== 'resolved' &&
             !t.is_escalated
      );
      if (criticalAtRisk.length > 0) {
        baseInsights.unshift({
          id:            'helpdesk:hd.escalate_critical',
          key:           'hd.escalate_critical',
          title:         'Critical Tickets Require Escalation',
          message:       `Ticket #${criticalAtRisk[0].ticket_number} is highly urgent but not escalated. AI suggests senior engineering review.`,
          icon:          'MdPriorityHigh',
          severity:      'critical',
          action:        'Escalate to Senior',
          actionType:    'escalate',
          actionPayload: { ticketId: criticalAtRisk[0].id },
          metrics:       { count: criticalAtRisk.length }, // --- UPDATE: measured, not a fixed 95
        });
      }
      // --- END UPDATE ---

      setAiInsights(baseInsights);
    } catch (error) {
      logger.log('ai:generate:error', { error: error.message }, 'error');
    } finally {
      setIsAiLoading(false);
    }
  }, [tickets, stats, logger]);

  useEffect(() => {
    if (!loading && tickets.length > 0 && stats) generateAiInsights();
  }, [loading, tickets.length, stats, generateAiInsights]);

  // ── AI ACTION ROUTER (Doc 1 switchboard) ──────────────────────────────────
  const handleAiAction = useCallback(async (insight) => {
    if (!insight || !insight.actionType) return;

    // 1. Handle UI Filters
    if (insight.actionType === 'filter') {
      const { filter, value } = insight.actionPayload;
      setFilters(prev => ({ ...prev, [filter]: value }));
      toast({
        title:       'Filter Applied',
        description: insight.title,
        status:      'success',
        duration:    2000,
        isClosable:  true,
      });
    }

    // 2. Handle Auto-Assign (ML Dispatch Optimizer)
    else if (insight.actionType === 'auto_assign') {
      const ticketIds = insight.actionPayload?.ticketIds || [];
      if (!ticketIds.length) return;

      toast({
        title:       'Optimizing Routes...',
        description: 'AI is finding the best agents.',
        status:      'info',
        duration:    2000,
      });
      setSubmitting(true);

      try {
        const response = await api.post('/helpdesk/auto-assign', { ticket_ids: ticketIds });
        if (response.data.success) {
          toast({
            title:       'Auto-Assignment Complete',
            description: `Successfully routed ${ticketIds.length} tickets to optimal agents.`,
            status:      'success',
            duration:    4000,
            isClosable:  true,
          });
          fetchTickets(true);
        }
      } catch (error) {
        toast({
          title:       'Auto-Assign Failed',
          description: error.response?.data?.message || 'Failed to route tickets.',
          status:      'error',
          duration:    5000,
          isClosable:  true,
        });
      } finally {
        setSubmitting(false);
      }
    }

    // --- UPDATE (AI Insights v3): navigate / retrain / rate
    else if (insight.actionType === 'navigate' && insight.actionPayload?.route) {
      window.history.pushState({}, '', insight.actionPayload.route);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
    else if (insight.actionType === 'retrain') {
      try {
        await phoneHelpdeskApi.retrain();
        toast({ title: 'Retraining started', description: 'The report classifier is learning from the latest verdicts.', status: 'success', duration: 3000 });
      } catch (error) {
        toast({ title: 'Retrain failed', description: error.response?.data?.message || error.message, status: 'error', duration: 5000 });
      }
    }
    else if (insight.actionType === 'rate') {
      try {
        await helpdeskAIInsightsService.rateInsight(insight.actionPayload?.insight || insight, insight.actionPayload?.rating || 1);
        toast({ title: 'Thanks for the feedback', status: 'success', duration: 2000 });
      } catch (_) { /* feedback is best effort */ }
    }
    // --- END UPDATE

    // 3. Handle Escalation (Doc 1: POST with body payload)
    else if (insight.actionType === 'escalate') {
      const ticketId = insight.actionPayload?.ticketId;
      if (!ticketId) return;

      setSubmitting(true);
      try {
        const response = await api.post(`/helpdesk/${ticketId}/escalate`, {
          escalation_level: 'critical',
          reason:           'AI Auto-Escalation to Senior Engineering',
        });

        if (response.data.success) {
          toast({
            title:       'Ticket Escalated',
            description: `Ticket #${ticketId} has been escalated to the Senior Engineering team.`,
            status:      'warning',
            duration:    5000,
            isClosable:  true,
          });
          fetchTickets(true);
          if (isDetailOpen && selectedTicket?.id === ticketId) {
            fetchTicketDetails(ticketId);
          }
        }
      } catch (error) {
        toast({
          title:       'Escalation Failed',
          description: error.response?.data?.message || 'Could not escalate ticket.',
          status:      'error',
          duration:    5000,
          isClosable:  true,
        });
      } finally {
        setSubmitting(false);
      }
    }
  }, [toast, fetchTickets, isDetailOpen, selectedTicket]);

  // ── GENERAL ACTIONS ────────────────────────────────────────────────────────
  const handleOpenTicket = async (ticket) => {
    setSelectedTicket(ticket);
    onDetailOpen();
    await fetchTicketDetails(ticket.id);
  };

  // --- UPDATE (Phone AI Helpdesk): deep links from notifications — /helpdesk?ticket=123&tab=trace
  useEffect(() => {
    try {
      const qs = new URLSearchParams(window.location.search);
      const tid = qs.get('ticket');
      if (tid && /^\d+$/.test(tid)) {
        handleOpenTicket({ id: Number(tid) });
        if (qs.get('tab') === 'trace') setSelectedTab(3);
      }
    } catch (_) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // --- END UPDATE

  // --- UPDATE (Work Orders link): /admin/helpdesk?work_order=<id> opens the ticket that raised that work order
  const pendingWoLinkRef = useRef((() => { try { return new URLSearchParams(window.location.search).get('work_order'); } catch (_) { return null; } })());
  useEffect(() => {
    const woId = pendingWoLinkRef.current;
    if (!woId || loading) return;
    const hit = tickets.find(t => t.work_order_id !== null && t.work_order_id !== undefined && String(t.work_order_id) === String(woId));
    pendingWoLinkRef.current = null;
    if (hit) { handleOpenTicket(hit); setSelectedTab(3); }
    else setFilters(prev => ({ ...prev, search: String(woId) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, tickets]);
  // --- END UPDATE

  // --- UPDATE (Doc 2): handleOpenAssignModal ---
  const handleOpenAssignModal = (ticket) => {
    setSelectedTicket(ticket);
    setSelectedAgentId(ticket.assigned_agent_id || '');
    onAssignOpen();
  };
  // --- END UPDATE ---

  const handleAssignTicket = async () => {
    if (!selectedTicket) return;
    setSubmitting(true);
    try {
      const response = await api.put(`/helpdesk/${selectedTicket.id}/assign`, {
        assigned_agent_id: selectedAgentId,
      });
      if (response.data.success) {
        toast({ title: 'Success', description: 'Ticket assigned successfully', status: 'success', duration: 3000 });
        onAssignClose();
        fetchTickets();
        if (isDetailOpen) fetchTicketDetails(selectedTicket.id);
      }
    } catch (error) {
      toast({
        title:       'Error',
        description: error.response?.data?.message || 'Failed to assign ticket',
        status:      'error',
        duration:    5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateWorkOrder = async () => {
    if (!selectedTicket) return;
    setSubmitting(true);
    try {
      // --- UPDATE (Phone AI Helpdesk): phone/public reports go through the report router so the
      // work order is linked to the road issue, the hot-spot cluster and every ticket in it.
      if (selectedTicket.public_report_id || selectedTicket.source === 'phone') {
        let reportId = selectedTicket.public_report_id;
        if (!reportId) {
          try { reportId = (await phoneHelpdeskApi.traceByTicket(selectedTicket.id))?.report?.id; } catch (_) { reportId = null; }
        }
        if (reportId) {
          const wo = await phoneHelpdeskApi.createWorkOrder(reportId);
          toast({ title: wo.reused ? 'Linked to existing work order' : 'Work Order Dispatched', description: `Ticket ${selectedTicket.ticket_number}`, status: 'success', duration: 4000, isClosable: true });
          fetchTicketDetails(selectedTicket.id);
          fetchTickets(true);
          return;
        }
      }
      // --- END UPDATE
      const woPayload = {
        title:       `[HD-${selectedTicket.ticket_number}] ${selectedTicket.subject}`,
        description: selectedTicket.description,
        priority:    selectedTicket.priority,
        category:    'Corrective Maintenance',
        source:      'helpdesk',
        issue_id:    selectedTicket.id,
      };
      // --- UPDATE: work_orders.issue_id is a foreign key to issues.id — a helpdesk ticket id there
      // fails the FK (or silently links an unrelated issue). The ticket is referenced in the title.
      delete woPayload.issue_id;
      // --- END UPDATE
      const response = await api.post('/workorders', woPayload);
      if (response.data) {
        await api.put(`/helpdesk/${selectedTicket.id}`, {
          work_order_id: response.data.workOrder?.id || response.data.data?.id || response.data.id, // --- UPDATE: tolerate response shapes
        });
        toast({
          title:       'Work Order Dispatched',
          description: `WO created for ticket ${selectedTicket.ticket_number}.`,
          status:      'success',
          duration:    4000,
          isClosable:  true,
        });
        fetchTicketDetails(selectedTicket.id);
        fetchTickets(true);
      }
    } catch (error) {
      toast({
        title:       'Dispatch Failed',
        description: error.response?.data?.message || 'Failed to create linked Work Order',
        status:      'error',
        duration:    5000,
        isClosable:  true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicketForm.subject || newTicketForm.subject.length < 5) {
      return toast({
        title:       'Validation Error',
        description: 'Subject must be at least 5 characters',
        status:      'warning',
        duration:    3000,
      });
    }
    setSubmitting(true);
    try {
      const response = await api.post('/helpdesk', newTicketForm);
      if (response.data.success) {
        toast({
          title:       'Success',
          description: `Ticket ${response.data.data.ticket_number} created`,
          status:      'success',
          duration:    5000,
          isClosable:  true,
        });
        setNewTicketForm({ subject: '', description: '', category: 'general_inquiry', priority: 'medium' });
        onNewTicketClose();
        fetchTickets();
      }
    } catch (error) {
      toast({
        title:       'Error',
        description: error.response?.data?.message || 'Failed to create ticket',
        status:      'error',
        duration:    5000,
        isClosable:  true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) {
      return toast({
        title:       'Validation Error',
        description: 'Message cannot be empty',
        status:      'warning',
        duration:    3000,
      });
    }
    setSubmitting(true);
    try {
      const response = await api.post(`/helpdesk/${selectedTicket.id}/reply`, {
        message:     replyText,
        is_internal: isInternalNote,
      });
      if (response.data.success) {
        toast({ title: 'Success', description: 'Reply sent successfully', status: 'success', duration: 3000 });
        const newReply = response.data.data;
        if (newReply) {
          setMessages(prev =>
            prev.some(m => m.id === newReply.id) ? prev : [...prev, newReply]
          );
          setTimeout(() => {
            const c = document.getElementById('chat-container');
            if (c) c.scrollTop = c.scrollHeight;
          }, 100);
        }
        setReplyText('');
        setIsInternalNote(false);
        fetchTickets(true);
      }
    } catch (error) {
      toast({
        title:       'Error',
        description: error.response?.data?.message || 'Failed to send reply',
        status:      'error',
        duration:    5000,
        isClosable:  true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // --- UPDATE (Doc 2): handleResolveTicket ---
  const handleResolveTicket = async () => {
    setSubmitting(true);
    try {
      const response = await api.put(`/helpdesk/${selectedTicket.id}/resolve`);
      if (response.data.success) {
        toast({ title: 'Success', description: 'Ticket resolved successfully', status: 'success', duration: 3000 });
        onDetailClose();
        fetchTickets();
      }
    } catch (error) {
      toast({
        title:       'Error',
        description: error.response?.data?.message || 'Failed to resolve ticket',
        status:      'error',
        duration:    5000,
        isClosable:  true,
      });
    } finally {
      setSubmitting(false);
    }
  };
  // --- END UPDATE ---

  const handleGenerateAiReply = async () => {
    if (!selectedTicket) return;
    setIsGeneratingReply(true);
    try {
      const draft = await aiService.generateHelpdeskReplyDraft(selectedTicket.id);
      setReplyText(draft);
      toast({
        title:       '✨ AI Draft Generated',
        description: 'Review and edit before sending.',
        status:      'success',
        duration:    3000,
        isClosable:  true,
        position:    'top-right',
      });
    } catch (error) {
      toast({
        title:       'AI Generation Failed',
        description: 'Ensure the local AI engine is running.',
        status:      'error',
        duration:    4000,
      });
    } finally {
      setIsGeneratingReply(false);
    }
  };

  // --- UPDATE (Doc 2): handleFilterChange + clearFilters ---
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const clearFilters = () => {
    setFilters({ status: '', priority: '', category: '', search: '', my_tickets: false });
  };
  // --- END UPDATE ---

  // --- UPDATE (Doc 2): handleRefresh with toast ---
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await fetchTickets();
    setIsRefreshing(false);
    toast({
      title:       'Refreshed',
      description: 'Ticket list updated',
      status:      'success',
      duration:    2000,
      isClosable:  true,
      position:    'bottom-right',
    });
  }, [fetchTickets, toast]);
  // --- END UPDATE ---

  useEffect(() => { fetchTickets(); fetchAgents(); }, [fetchTickets, fetchAgents]);

  const hasActiveFilters = useMemo(
    () => Object.values(filters).some(val => val !== '' && val !== false),
    [filters]
  );
  const filteredTickets = useMemo(() => tickets, [tickets]);

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <MotionBox
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      pt={{ base: '130px', md: '80px', xl: '80px' }}
      bg={bgColor}
      minH="100vh"
    >
      <Flex direction="column" maxW="1600px" mx="auto" px={{ base: 4, md: 6 }}>

        {/* ── HEADER ────────────────────────────────────────────────────────── */}
        <MotionFlex
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          justify="space-between"
          mb={8}
          align="center"
          flexWrap="wrap"
          gap={4}
        >
          <Box>
            <Flex align="center" gap={3} mb={2}>
              <MotionBox
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                bgGradient={headerGradient}
                p={3}
                borderRadius="xl"
                boxShadow="lg"
              >
                <Icon as={MdWaves} color="white" boxSize={7} />
              </MotionBox>
              <Box>
                <HStack spacing={3}>
                  <Text
                    fontSize={isMobile ? '2xl' : '3xl'}
                    fontWeight="800"
                    bgGradient={headerGradient}
                    bgClip="text"
                  >
                    Helpdesk Support
                  </Text>
                  <ConnectionIndicator isConnected={wsConnected} />
                  {/* --- UPDATE (Doc 2): AI active indicator --- */}
                  {isAiLoading && <AITypingIndicator />}
                  {/* --- END UPDATE --- */}
                </HStack>
                <Text fontSize="sm" color={mutedTextColor} fontWeight="500">
                  Manage support tickets with real-time AI assistance
                  {lastWsUpdate && ` • Last sync: ${formatTimeAgo(lastWsUpdate)}`}
                </Text>
              </Box>
            </Flex>
          </Box>

          <HStack spacing={3}>
            <Tooltip label={showAiPanel ? 'Hide AI Insights' : 'Show AI Insights'} hasArrow>
              <Button
                leftIcon={<Icon as={showAiPanel ? MdClose : MdPsychology} />}
                onClick={() => setShowAiPanel(!showAiPanel)}
                colorScheme={showAiPanel ? 'gray' : 'purple'}
                variant={showAiPanel ? 'ghost' : 'solid'}
                borderRadius="xl"
              >
                {!isMobile && (showAiPanel ? 'Hide' : 'AI Insights')}
              </Button>
            </Tooltip>
            <Tooltip label="Refresh tickets" hasArrow>
              <IconButton
                icon={<MdRefresh />}
                onClick={handleRefresh}
                isLoading={isRefreshing}
                variant="ghost"
                colorScheme="blue"
                aria-label="Refresh"
                borderRadius="xl"
              />
            </Tooltip>
            <Button
              leftIcon={<MdAdd />}
              colorScheme="blue"
              onClick={onNewTicketOpen}
              borderRadius="xl"
              boxShadow="md"
              _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
              transition="all 0.2s"
            >
              {!isMobile && 'New Ticket'}
            </Button>
          </HStack>
        </MotionFlex>

        {/* --- UPDATE (Phone AI Helpdesk): 24-hour phone AI command center --- */}
        <HStack justify="flex-end" mb={2}>
          <Button size="xs" variant="ghost" leftIcon={<Icon as={MdSmartToy} />} onClick={togglePhoneCenter}>
            {showPhoneCenter ? 'Hide' : 'Show'} Phone AI Command Center
          </Button>
        </HStack>
        {showPhoneCenter && !isDetailOpen && (
          <PhoneAICommandCenter onOpenTicket={(id) => handleOpenTicket({ id })} />
        )}
        {/* --- END UPDATE --- */}

        {/* ── STATS ─────────────────────────────────────────────────────────── */}
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={5} mb={6}>
          {[
            {
              label:  'Total Tickets',
              value:  stats?.total || 0,
              icon:   MdDashboard,
              color:  'blue',
              onClick: () => handleFilterChange('status', ''),
            },
            {
              label:  'Active',
              value:  stats?.active_tickets || 0,
              icon:   MdAccessTime,
              color:  'cyan',
              onClick: () => handleFilterChange('status', 'open'),
            },
            {
              label:       'SLA Breached',
              value:       stats?.breached || 0,
              icon:        MdWarning,
              color:       'red',
              onClick:     null,
              aiHighlight: (stats?.breached || 0) > 0,
            },
            {
              label:  'Resolved',
              value:  stats?.resolved_tickets || 0,
              icon:   MdCheckCircle,
              color:  'green',
              onClick: () => handleFilterChange('status', 'resolved'),
            },
          ].map((stat, index) => (
            <MotionBox
              key={stat.label}
              custom={index}
              variants={statsVariants}
              initial="initial"
              animate="animate"
            >
              <EnhancedStatsCard {...stat} isLoading={loading} />
            </MotionBox>
          ))}
        </SimpleGrid>

        {/* ── GLOBAL AI PANEL (hidden when detail modal is open) ──────────── */}
        {/* --- UPDATE (Doc 1 + Doc 2): display toggle on modal state --- */}
        <Box display={isDetailOpen ? 'none' : 'block'}>
          <HelpdeskAIPanel
            isVisible={showAiPanel}
            insights={aiInsights}
            selectedTicket={null}
            isLoading={isAiLoading}
            onRefresh={() => generateAiInsights(true)} // --- UPDATE: refresh bypasses caches
            onClose={() => setShowAiPanel(false)}
            onAction={handleAiAction}
          />
        </Box>
        {/* --- END UPDATE --- */}

        {/* ── FILTERS ───────────────────────────────────────────────────────── */}
        <MotionBox
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Box
            bg={cardBgColor}
            p={5}
            borderRadius="2xl"
            boxShadow="md"
            mb={6}
            borderWidth="1px"
            borderColor={borderColor}
          >
            <HStack spacing={4} mb={4} align="center">
              <Icon as={MdFilterList} boxSize={5} color="blue.500" />
              <Text fontWeight="700" fontSize="lg" color={textColor}>Filters</Text>
              {hasActiveFilters && (
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  onClick={clearFilters}
                  leftIcon={<MdClose />}
                  borderRadius="full"
                >
                  Clear All
                </Button>
              )}
            </HStack>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm" color={textColor} fontWeight="600">Search</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <MdSearch color="gray" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search tickets..."
                    value={filters.search}
                    onChange={e => handleFilterChange('search', e.target.value)}
                    bg={inputBg}
                    borderRadius="xl"
                  />
                </InputGroup>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" color={textColor} fontWeight="600">Status</FormLabel>
                <Select
                  value={filters.status}
                  onChange={e => handleFilterChange('status', e.target.value)}
                  bg={inputBg}
                  borderRadius="xl"
                >
                  <option value="">All Statuses</option>
                  {Object.keys(STATUS_CONFIG).map(s => (
                    <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" color={textColor} fontWeight="600">Priority</FormLabel>
                <Select
                  value={filters.priority}
                  onChange={e => handleFilterChange('priority', e.target.value)}
                  bg={inputBg}
                  borderRadius="xl"
                >
                  <option value="">All Priorities</option>
                  {Object.keys(PRIORITY_CONFIG).map(p => (
                    <option key={p} value={p}>{PRIORITY_CONFIG[p].label}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" color={textColor} fontWeight="600">Category</FormLabel>
                <Select
                  value={filters.category}
                  onChange={e => handleFilterChange('category', e.target.value)}
                  bg={inputBg}
                  borderRadius="xl"
                >
                  <option value="">All Categories</option>
                  {CATEGORY_OPTIONS.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl display="flex" alignItems="flex-end">
                <Button
                  w="full"
                  variant={filters.my_tickets ? 'solid' : 'outline'}
                  colorScheme="blue"
                  onClick={() => handleFilterChange('my_tickets', !filters.my_tickets)}
                  leftIcon={<MdPerson />}
                  borderRadius="xl"
                >
                  My Tickets
                </Button>
              </FormControl>
            </SimpleGrid>
          </Box>
        </MotionBox>

        {/* ── TICKETS TABLE ─────────────────────────────────────────────────── */}
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Box
            bg={cardBgColor}
            borderRadius="2xl"
            boxShadow="xl"
            overflow="hidden"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <Box h="3px" bgGradient={headerGradient} />
            <Box overflowX="auto">
              <Table variant="simple" size="md">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th color={textColor} fontWeight="700">Ticket ID</Th>
                    <Th color={textColor} fontWeight="700">Subject</Th>
                    <Th color={textColor} fontWeight="700">Priority</Th>
                    <Th color={textColor} fontWeight="700">Status</Th>
                    <Th color={textColor} fontWeight="700">SLA + Risk</Th>
                    <Th color={textColor} fontWeight="700">AI Tags</Th>
                    <Th color={textColor} fontWeight="700">Created</Th>
                    <Th color={textColor} fontWeight="700">Messages</Th>
                    <Th color={textColor} fontWeight="700">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <Tr key={i}>
                        {Array.from({ length: 9 }).map((_, j) => (
                          <Td key={j}><Skeleton height="20px" borderRadius="md" /></Td>
                        ))}
                      </Tr>
                    ))
                  ) : filteredTickets.length === 0 ? (
                    <Tr>
                      <Td colSpan={9}>
                        <Box textAlign="center" py={10}>
                          <Icon as={MdMessage} boxSize={12} color="gray.400" mb={4} />
                          <Text color={mutedTextColor} fontSize="lg" fontWeight="600" mb={2}>
                            No tickets found
                          </Text>
                          <Text color={mutedTextColor} fontSize="sm">
                            {hasActiveFilters
                              ? 'Try adjusting your filters'
                              : 'Create your first ticket to get started'}
                          </Text>
                        </Box>
                      </Td>
                    </Tr>
                  ) : (
                    <AnimatePresence>
                      {filteredTickets.map((ticket, index) => (
                        <MotionTr
                          key={ticket.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ delay: index * 0.03 }}
                          style={{ cursor: 'pointer' }}
                          whileHover={{ backgroundColor: tableHoverBg }}
                          onClick={() => handleOpenTicket(ticket)}
                        >
                          <Td>
                            <VStack align="flex-start" spacing={1}>
                              <Text fontWeight="bold" fontSize="sm" color="blue.600">
                                {ticket.ticket_number}
                              </Text>
                              {/* --- UPDATE (Doc 2): AI sentiment on ticket row --- */}
                              <AISentimentBadge
                                text={`${ticket.subject || ''} ${ticket.description || ''}`}
                              />
                              {/* --- END UPDATE --- */}
                            </VStack>
                          </Td>
                          <Td>
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="600" fontSize="sm" noOfLines={1} color={textColor}>
                                {ticket.subject}
                              </Text>
                              <Text fontSize="xs" color={mutedTextColor}>
                                by {ticket.requester_name_user || ticket.requester_name || 'Unknown'}
                              </Text>
                            </VStack>
                          </Td>
                          <Td><PriorityBadge priority={ticket.priority} /></Td>
                          <Td><StatusBadge   status={ticket.status}    /></Td>
                          <Td><SLAIndicator  ticket={ticket}           /></Td>
                          {/* --- UPDATE (Doc 2): AI topic tags column --- */}
                          <Td><AITopicTags ticket={ticket} maxTags={2} /></Td>
                          {/* --- END UPDATE --- */}
                          <Td>
                            <Tooltip label={new Date(ticket.created_at).toLocaleString()}>
                              <Text fontSize="sm" color={mutedTextColor}>
                                {formatTimeAgo(ticket.created_at)}
                              </Text>
                            </Tooltip>
                          </Td>
                          <Td>
                            <HStack spacing={1}>
                              <Icon as={MdMessage} boxSize={4} color="gray.500" />
                              <Text fontSize="sm" fontWeight="600" color={textColor}>
                                {ticket.message_count || 0}
                              </Text>
                            </HStack>
                          </Td>
                          <Td>
                            <HStack spacing={2}>
                              <Tooltip label="View Details" hasArrow>
                                <IconButton
                                  icon={<MdVisibility />}
                                  size="sm"
                                  variant="ghost"
                                  colorScheme="blue"
                                  onClick={e => { e.stopPropagation(); handleOpenTicket(ticket); }}
                                  aria-label="View"
                                  borderRadius="lg"
                                />
                              </Tooltip>
                              <Menu>
                                <MenuButton
                                  as={IconButton}
                                  icon={<MdMoreVert />}
                                  size="sm"
                                  variant="ghost"
                                  onClick={e => e.stopPropagation()}
                                  aria-label="More options"
                                  borderRadius="lg"
                                />
                                <MenuList>
                                  <MenuItem icon={<MdEdit />}>Edit</MenuItem>
                                  <MenuItem
                                    icon={<MdAssignment />}
                                    onClick={e => { e.stopPropagation(); handleOpenAssignModal(ticket); }}
                                  >
                                    Assign
                                  </MenuItem>
                                  <MenuItem icon={<MdCheckCircle />}>Resolve</MenuItem>
                                  <Divider />
                                  <MenuItem icon={<MdDelete />} color="red.500">Delete</MenuItem>
                                </MenuList>
                              </Menu>
                            </HStack>
                          </Td>
                        </MotionTr>
                      ))}
                    </AnimatePresence>
                  )}
                </Tbody>
              </Table>
            </Box>

            {/* ── Pagination ─────────────────────────────────────────────── */}
            {!loading && filteredTickets.length > 0 && (
              <Flex
                justify="space-between"
                align="center"
                p={4}
                borderTop="1px"
                borderColor={borderColor}
                bg={tableHeaderBg}
              >
                <Text fontSize="sm" color={mutedTextColor} fontWeight="500">
                  Showing {pagination.offset + 1} to{' '}
                  {Math.min(pagination.offset + pagination.limit, pagination.total)} of{' '}
                  {pagination.total}
                </Text>
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setPagination(prev => ({
                        ...prev,
                        offset: Math.max(0, prev.offset - prev.limit),
                      }))
                    }
                    isDisabled={pagination.offset === 0}
                    borderRadius="lg"
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))
                    }
                    isDisabled={pagination.offset + pagination.limit >= pagination.total}
                    borderRadius="lg"
                  >
                    Next
                  </Button>
                </HStack>
              </Flex>
            )}
          </Box>
        </MotionBox>
      </Flex>

      {/* ── TICKET DETAIL MODAL ───────────────────────────────────────────────── */}
      <Modal isOpen={isDetailOpen} onClose={onDetailClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.600" />
        <ModalContent maxH="90vh" borderRadius="2xl">
          <Box h="3px" bgGradient={headerGradient} borderTopRadius="2xl" />

          <ModalHeader borderBottom="1px" borderColor={borderColor}>
            <VStack align="start" spacing={2}>
              <HStack spacing={3} flexWrap="wrap">
                <Text fontSize="xl" fontWeight="bold" color={textColor}>
                  {selectedTicket?.ticket_number}
                </Text>
                <StatusBadge   status={selectedTicket?.status}   />
                <PriorityBadge priority={selectedTicket?.priority} />
                {/* --- UPDATE (Doc 2): AI sentiment in modal header --- */}
                <AISentimentBadge
                  text={`${selectedTicket?.subject || ''} ${selectedTicket?.description || ''}`}
                />
                {/* --- END UPDATE --- */}
              </HStack>
              <Text fontSize="lg" fontWeight="normal" color={mutedTextColor}>
                {selectedTicket?.subject}
              </Text>

              {/* --- UPDATE (Doc 2): AI topic tags in modal header --- */}
              <AITopicTags ticket={selectedTicket || {}} maxTags={4} />
              {/* --- END UPDATE --- */}

              {selectedTicket && selectedTicket.sla_status !== 'no_sla' && (
                <HStack spacing={2}>
                  <SLAIndicator ticket={selectedTicket} />
                  <Text fontSize="xs" color={mutedTextColor}>
                    {selectedTicket.time_remaining_formatted} remaining
                  </Text>
                </HStack>
              )}
            </VStack>
          </ModalHeader>

          {/* --- UPDATE (Doc 2): ModalBody with depth bg + AI strips --- */}
          <ModalBody p={0} bg={modalBodyBg}>
            <Box px={6} pt={4}>
              {/* AI Analysis Strip */}
              {selectedTicket && <AIAnalysisStrip ticket={selectedTicket} />}
            </Box>

            {/* Ticket-specific Ollama RAG Analysis */}
            <Box px={6} pt={2}>
              <HelpdeskAIPanel selectedTicket={selectedTicket} isVisible={true} />
            </Box>
            {/* --- END UPDATE --- */}

            {/* Work Order Card */}
            {selectedTicket?.work_order_id && (
              <Box px={6} pt={4} pb={0}>
                <TicketWorkOrderCard workOrderId={selectedTicket.work_order_id} />
              </Box>
            )}

            <Tabs index={selectedTab} onChange={setSelectedTab} colorScheme="blue">
              <TabList px={6} pt={4}>
                <Tab fontWeight="600">Discussion</Tab>
                <Tab fontWeight="600">Details</Tab>
                {/* --- UPDATE (Doc 2): AI Actions tab --- */}
                <Tab fontWeight="600">
                  <HStack spacing={1}>
                    <Icon as={MdSmartToy} />
                    <Text>AI Actions</Text>
                  </HStack>
                </Tab>
                {/* --- END UPDATE --- */}
                {/* --- UPDATE (Phone AI Helpdesk): start-to-finish trace --- */}
                <Tab fontWeight="600">
                  <HStack spacing={1}>
                    <Icon as={MdTimer} />
                    <Text>Report Trace</Text>
                  </HStack>
                </Tab>
                {/* --- END UPDATE --- */}
              </TabList>

              <TabPanels>
                {/* ── Discussion Tab ───────────────────────────────────────── */}
                <TabPanel>
                  <Box bg={hoverBg} p={4} borderRadius="xl" mb={4}>
                    <Text fontWeight="bold" mb={2} color={textColor}>Description:</Text>
                    <Text fontSize="sm" color={mutedTextColor} whiteSpace="pre-wrap">
                      {selectedTicket?.description || 'No description provided'}
                    </Text>
                  </Box>
                  <Text fontWeight="bold" mb={3} color={textColor}>Discussion:</Text>

                  {loadingMessages ? (
                    <VStack spacing={3}>
                      {Array.from({ length: 3 }).map((_, i) => (
                        <SkeletonText key={i} noOfLines={3} spacing={2} w="full" />
                      ))}
                    </VStack>
                  ) : (
                    <VStack
                      id="chat-container"
                      align="stretch"
                      spacing={3}
                      maxH="400px"
                      overflowY="auto"
                      pr={2}
                      css={{
                        '&::-webkit-scrollbar':             { width: '8px'                               },
                        '&::-webkit-scrollbar-track':       { background: '#f1f1f1', borderRadius: '4px' },
                        '&::-webkit-scrollbar-thumb':       { background: '#888',    borderRadius: '4px' },
                        '&::-webkit-scrollbar-thumb:hover': { background: '#555'                         },
                      }}
                    >
                      {messages.length === 0 ? (
                        <Box textAlign="center" py={8}>
                          <Icon as={MdMessage} boxSize={10} color="gray.400" mb={2} />
                          <Text color={mutedTextColor}>No messages yet</Text>
                        </Box>
                      ) : (
                        messages.map(msg => (
                          <MessageBubble
                            key={msg.id}
                            message={msg}
                            isAgent={msg.sender_role === 'admin' || msg.sender_role === 'staff'}
                          />
                        ))
                      )}
                    </VStack>
                  )}

                  <Divider my={4} />

                  <VStack spacing={3} align="stretch">
                    <HStack justify="space-between" align="center">
                      <Text fontSize="sm" fontWeight="600" color={textColor}>Your Reply:</Text>
                      <Button
                        size="xs"
                        colorScheme="purple"
                        variant="subtle"
                        leftIcon={<Icon as={MdAutoAwesome} />}
                        onClick={handleGenerateAiReply}
                        isLoading={isGeneratingReply}
                        loadingText="AI thinking..."
                        borderRadius="full"
                      >
                        ✨ Auto-Draft Reply
                      </Button>
                    </HStack>
                    <Textarea
                      placeholder="Type your reply..."
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      rows={4}
                      resize="vertical"
                      bg={inputBg}
                      borderRadius="xl"
                      focusBorderColor="purple.400"
                    />
                    <HStack justify="space-between">
                      <IconButton
                        icon={<MdAttachFile />}
                        size="sm"
                        variant="ghost"
                        aria-label="Attach file"
                        borderRadius="lg"
                      />
                      <Button
                        leftIcon={<MdSend />}
                        colorScheme="blue"
                        size="sm"
                        onClick={handleSendReply}
                        isLoading={submitting}
                        isDisabled={!replyText.trim()}
                        borderRadius="xl"
                      >
                        Send Reply
                      </Button>
                    </HStack>
                  </VStack>
                </TabPanel>

                {/* ── Details Tab ─────────────────────────────────────────── */}
                <TabPanel>
                  <SimpleGrid columns={2} spacing={4}>
                    {[
                      { label: 'Category',      value: selectedTicket?.category                                                          },
                      { label: 'Requester',     value: selectedTicket?.requester_name_user || 'Unknown'                                  },
                      { label: 'Created',       value: selectedTicket?.created_at
                          ? new Date(selectedTicket.created_at).toLocaleString() : 'N/A'                                                 },
                      { label: 'Last Updated',  value: selectedTicket?.updated_at
                          ? new Date(selectedTicket.updated_at).toLocaleString() : 'N/A'                                                 },
                      { label: 'Assigned Agent',value: selectedTicket?.agent_name || 'Unassigned'                                        },
                      { label: 'Watchers',      value: selectedTicket?.watcher_count || 0                                                },
                    ].map(({ label, value }) => (
                      <Box key={label}>
                        <Text fontSize="xs" color={mutedTextColor} fontWeight="medium" mb={1}>
                          {label}
                        </Text>
                        <Text fontWeight="semibold" color={textColor}>{value}</Text>
                      </Box>
                    ))}
                  </SimpleGrid>
                </TabPanel>

                {/* ── AI Actions Tab ──────────────────────────────────────── */}
                {/* --- UPDATE (Doc 2): full AI actions tab --- */}
                <TabPanel>
                  <VStack align="stretch" spacing={4}>
                    <AIQuickActionChips
                      ticket={selectedTicket}
                      onCreateWorkOrder={handleCreateWorkOrder}
                      onReply={handleGenerateAiReply}
                    />

                    {selectedTicket && (
                      <Box
                        p={4}
                        bg={aiBoxBg}
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor={aiBoxBc}
                      >
                        <HStack mb={3} spacing={2}>
                          <Icon as={MdInsights} color="blue.500" boxSize={5} />
                          <Text fontWeight="bold" fontSize="sm" color={aiBoxLabel}>
                            AI Ticket Analysis
                          </Text>
                        </HStack>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} fontSize="sm">
                          <Box>
                            <Text color={mutedTextColor} fontSize="xs" mb={1}>Detected Sentiment</Text>
                            <AISentimentBadge
                              text={`${selectedTicket.subject || ''} ${selectedTicket.description || ''}`}
                            />
                          </Box>
                          <Box>
                            <Text color={mutedTextColor} fontSize="xs" mb={1}>SLA Breach Risk</Text>
                            <Badge
                              colorScheme={
                                {
                                  breached: 'red',
                                  high:     'red',
                                  medium:   'orange',
                                  elevated: 'yellow',
                                  low:      'green',
                                }[deriveBreachRisk(selectedTicket)] || 'gray'
                              }
                              borderRadius="full"
                              px={3}
                            >
                              {deriveBreachRisk(selectedTicket).toUpperCase()}
                            </Badge>
                          </Box>
                          <Box>
                            <Text color={mutedTextColor} fontSize="xs" mb={1}>Detected Topics</Text>
                            <AITopicTags ticket={selectedTicket} maxTags={5} />
                          </Box>
                          <Box>
                            <Text color={mutedTextColor} fontSize="xs" mb={1}>Message Count</Text>
                            <Text fontWeight="semibold" color={textColor}>
                              {messages.length} messages
                            </Text>
                          </Box>
                        </SimpleGrid>
                      </Box>
                    )}
                  </VStack>
                </TabPanel>
                {/* --- END UPDATE --- */}
                {/* --- UPDATE (Phone AI Helpdesk): Report Trace tab --- */}
                <TabPanel>
                  {selectedTicket?.id && selectedTab === 3 && (
                    <ReportTracePanel
                      ticketId={selectedTicket.id}
                      onChanged={() => { fetchTicketDetails(selectedTicket.id); fetchTickets(true); }}
                    />
                  )}
                </TabPanel>
                {/* --- END UPDATE --- */}
              </TabPanels>
            </Tabs>
          </ModalBody>

          <ModalFooter
            borderTop="1px"
            borderColor={borderColor}
            bg={cardBgColor}
            borderBottomRadius="2xl"
          >
            <HStack spacing={3} flexWrap="wrap">
              <Button variant="ghost" onClick={onDetailClose} borderRadius="xl">Close</Button>

              {selectedTicket && !selectedTicket.work_order_id && (
                <Button
                  colorScheme="orange"
                  variant="outline"
                  leftIcon={<MdBuild />}
                  onClick={handleCreateWorkOrder}
                  isLoading={submitting}
                  borderRadius="xl"
                >
                  Create Work Order
                </Button>
              )}

              {selectedTicket?.work_order_id && (
                <Button
                  colorScheme="orange"
                  variant="solid"
                  leftIcon={<MdBuild />}
                  onClick={() =>
                    window.open(`/work-orders/${selectedTicket.work_order_id}`, '_blank')
                  }
                  borderRadius="xl"
                >
                  View Linked WO
                </Button>
              )}

              {/* --- UPDATE (Doc 2): Resolve Ticket button --- */}
              {selectedTicket?.status !== 'resolved' &&
               selectedTicket?.status !== 'closed' && (
                <Button
                  colorScheme="green"
                  leftIcon={<MdCheckCircle />}
                  onClick={handleResolveTicket}
                  isLoading={submitting}
                  borderRadius="xl"
                >
                  Resolve Ticket
                </Button>
              )}
              {/* --- END UPDATE --- */}
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── NEW TICKET MODAL ──────────────────────────────────────────────────── */}
      <Modal isOpen={isNewTicketOpen} onClose={onNewTicketClose} size="xl">
        <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.600" />
        <ModalContent borderRadius="2xl">
          <Box h="3px" bgGradient={headerGradient} borderTopRadius="2xl" />
          <ModalHeader color={textColor}>Create New Ticket</ModalHeader>
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel color={textColor} fontWeight="600">Subject</FormLabel>
                <Input
                  placeholder="Brief description of your issue"
                  value={newTicketForm.subject}
                  onChange={e => setNewTicketForm(prev => ({ ...prev, subject: e.target.value }))}
                  bg={inputBg}
                  borderRadius="xl"
                />
              </FormControl>

              {/* --- UPDATE (Doc 2): live AI sentiment preview on new ticket --- */}
              {newTicketForm.subject.length > 5 && (
                <HStack spacing={2}>
                  <Icon as={MdSmartToy} color="purple.400" boxSize={3} />
                  <Text fontSize="xs" color={mutedTextColor}>AI Sentiment:</Text>
                  <AISentimentBadge
                    text={`${newTicketForm.subject} ${newTicketForm.description}`}
                  />
                </HStack>
              )}
              {/* --- END UPDATE --- */}

              <FormControl>
                <FormLabel color={textColor} fontWeight="600">Description</FormLabel>
                <Textarea
                  placeholder="Detailed description of your issue..."
                  value={newTicketForm.description}
                  onChange={e =>
                    setNewTicketForm(prev => ({ ...prev, description: e.target.value }))
                  }
                  rows={5}
                  bg={inputBg}
                  borderRadius="xl"
                />
              </FormControl>

              <SimpleGrid columns={2} spacing={4}>
                <FormControl isRequired>
                  <FormLabel color={textColor} fontWeight="600">Category</FormLabel>
                  <Select
                    value={newTicketForm.category}
                    onChange={e =>
                      setNewTicketForm(prev => ({ ...prev, category: e.target.value }))
                    }
                    bg={inputBg}
                    borderRadius="xl"
                  >
                    {CATEGORY_OPTIONS.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel color={textColor} fontWeight="600">Priority</FormLabel>
                  <Select
                    value={newTicketForm.priority}
                    onChange={e =>
                      setNewTicketForm(prev => ({ ...prev, priority: e.target.value }))
                    }
                    bg={inputBg}
                    borderRadius="xl"
                  >
                    {Object.keys(PRIORITY_CONFIG).map(p => (
                      <option key={p} value={p}>{PRIORITY_CONFIG[p].label}</option>
                    ))}
                  </Select>
                </FormControl>
              </SimpleGrid>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onNewTicketClose} borderRadius="xl">
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              leftIcon={<MdAdd />}
              onClick={handleCreateTicket}
              isLoading={submitting}
              borderRadius="xl"
            >
              Create Ticket
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── ASSIGN TICKET MODAL ───────────────────────────────────────────────── */}
      <Modal isOpen={isAssignOpen} onClose={onAssignClose} size="md">
        <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.600" />
        <ModalContent borderRadius="2xl">
          <Box h="3px" bgGradient={headerGradient} borderTopRadius="2xl" />
          <ModalHeader color={textColor}>Assign Ticket</ModalHeader>
          <ModalBody>
            <VStack spacing={4}>
              <Text color={textColor}>
                Assign ticket <b>{selectedTicket?.ticket_number}</b> to an agent.
              </Text>
              <FormControl>
                <FormLabel color={textColor} fontWeight="600">Select Agent</FormLabel>
                <Select
                  placeholder="Select an agent"
                  value={selectedAgentId}
                  onChange={e => setSelectedAgentId(e.target.value)}
                  bg={inputBg}
                  borderRadius="xl"
                >
                  {agents.map(agent => (
                    <option key={agent.id} value={agent.id}>
                      {agent.username || agent.email} ({agent.role})
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAssignClose} borderRadius="xl">Cancel</Button>
            <Button
              colorScheme="blue"
              onClick={handleAssignTicket}
              isLoading={submitting}
              isDisabled={!selectedAgentId}
              borderRadius="xl"
            >
              Assign
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </MotionBox>
  );
}